import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../../lib/utils'
import Spinner from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

// 각 variant 는 라이트/다크 모드에 따라 텍스트·테두리·호버 색을 분리.
//   dark:* 접두어는 tailwind.config.js 의 커스텀 variant ('html:not(.light) &') 로
//   html.light 클래스가 없을 때만 적용됨 (= 다크 모드 기본).
const variantClasses: Record<ButtonVariant, string> = {
  // ⚠️ 글자는 흰색이 아니라 **진한 잉크**다. amber-500 위의 흰 글씨는 2.15:1 로
  //    WCAG AA(4.5)에 한참 못 미친다(실측). 같은 배경에 zinc-900 을 얹으면 8.25:1 이고,
  //    hover 의 amber-400 에서는 더 올라간다. 앰버 버튼에 어두운 글자는 표준 처리다.
  primary:
    'bg-amber-500 hover:bg-amber-400 text-zinc-900 shadow-sm transition-colors duration-150',
  secondary: [
    'bg-zinc-100 text-zinc-700 border border-zinc-300',
    'hover:bg-zinc-200 hover:border-zinc-400 hover:text-zinc-900',
    'dark:bg-white/[0.08] dark:text-zinc-200 dark:border-white/[0.12]',
    'dark:hover:bg-white/[0.13] dark:hover:border-white/[0.22] dark:hover:text-white',
    'transition-all duration-200',
  ].join(' '),
  outline: [
    'bg-transparent text-zinc-700 border border-zinc-300',
    'hover:bg-zinc-100 hover:text-zinc-900 hover:border-zinc-400',
    'dark:text-zinc-300 dark:border-white/[0.12]',
    'dark:hover:bg-white/[0.06] dark:hover:text-white dark:hover:border-white/[0.24]',
    'transition-all duration-200',
  ].join(' '),
  ghost: [
    'bg-transparent text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
    'dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-white',
    'transition-all duration-200',
  ].join(' '),
  danger:
    'bg-red-600 hover:bg-red-500 text-white transition-colors duration-150',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-md',
  md: 'px-5 py-2.5 text-sm rounded-md',
  lg: 'px-7 py-3.5 text-base rounded-md',
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-semibold',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-deep',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <Spinner size="sm" />
        ) : leftIcon ? (
          <span className="shrink-0">{leftIcon}</span>
        ) : null}
        {children}
        {!isLoading && rightIcon && (
          <span className="shrink-0">{rightIcon}</span>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
